/**
 * Cart-drawer recommendations (CRO 29.10, "gap to free shipping").
 * 1) Cart below the free-shipping threshold: in-stock items from the gap collection
 *    (theme setting cart_gap_collection, fallback: all products capped at cart_gap_max_price),
 *    loaded once via /collections/<handle>?section_id=cart-gap-data&sort_by=best-selling.
 *    Per product: cheapest available variant that closes the gap, else its cheapest variant.
 *    Ranking: items that close the gap (closest first), then the cheapest; best-selling as tiebreak.
 * 2) Nothing fits: complementary, then related (Shopify recommendations), cheapest available variant.
 * 3) Cart at/over the threshold: block hidden.
 * Always excludes products already in the cart. Sale prices from window.activeSales (custom.active_sales).
 */
(function () {
  var pending = null;
  var debounceTimer = null;
  var gapCache = {};

  function currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'ILS';
  }

  function moneyFromCents(cents, cur) {
    try {
      return (cents / 100).toLocaleString('he-IL', {
        style: 'currency',
        currency: cur || currency(),
        minimumFractionDigits: cents % 100 ? 2 : 0,
        maximumFractionDigits: 2,
      });
    } catch (e) {
      return '₪' + cents / 100;
    }
  }

  // Same rule as snippets/price.liquid: single best live sale for the product, rounded to agorot.
  function saleOff(productId, cents) {
    var as = window.activeSales && window.activeSales.sales;
    var off = 0;
    if (!as || !as.length) return 0;
    var nowMs = Date.now();
    for (var k = 0; k < as.length; k++) {
      var s = as[k];
      if (nowMs < s.start || (s.end && nowMs >= s.end)) continue;
      if (!s.all && s.ids.indexOf(String(productId)) === -1) continue;
      var o =
        s.type === 'percentage'
          ? Math.round((cents * s.value) / 100)
          : s.type === 'fixed_amount'
          ? Math.min(cents, Math.round(s.value * 100))
          : 0;
      if (o > off) off = o;
    }
    return off;
  }

  function cartIdSet(root) {
    var raw = (root.getAttribute('data-cart-product-ids') || '').split(',');
    var set = {};
    for (var i = 0; i < raw.length; i++) {
      var id = String(raw[i] || '').trim();
      if (id) set[id] = true;
    }
    return set;
  }

  /* ---------- gap-to-free-shipping source ---------- */

  function loadGapData(source) {
    if (gapCache[source]) return gapCache[source];
    var url = source + (source.indexOf('?') >= 0 ? '&' : '?') + 'section_id=cart-gap-data&sort_by=best-selling';
    gapCache[source] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('gap ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-cart-gap-json]');
        if (!el) return [];
        var data = JSON.parse(el.textContent);
        return data.products || [];
      })
      .catch(function () {
        delete gapCache[source];
        return [];
      });
    return gapCache[source];
  }

  function pickGap(products, exclude, gap, limit, cap) {
    var closers = [];
    var others = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p || exclude[String(p.id)] || !p.v || !p.v.length) continue;
      var best = null; // cheapest variant that closes the gap
      var cheapest = null;
      for (var j = 0; j < p.v.length; j++) {
        var price = p.v[j][1];
        if (cap && price > cap) continue;
        var eff = price - saleOff(p.id, price);
        var row = { id: p.v[j][0], price: price, eff: eff };
        if (!cheapest || eff < cheapest.eff) cheapest = row;
        if (eff >= gap && (!best || eff < best.eff)) best = row;
      }
      if (!cheapest) continue;
      var item = {
        product: { id: p.id, title: p.t, url: p.u, featured_image: p.img },
        variant: best || cheapest,
        rank: p.r || 9999,
      };
      (best ? closers : others).push(item);
    }
    closers.sort(function (a, b) {
      return a.variant.eff - b.variant.eff || a.rank - b.rank;
    });
    others.sort(function (a, b) {
      return a.variant.eff - b.variant.eff || a.rank - b.rank;
    });
    return closers.concat(others).slice(0, limit);
  }

  /* ---------- Shopify recommendations fallback ---------- */

  function pickProducts(list, exclude, limit) {
    var out = [];
    for (var i = 0; i < (list || []).length && out.length < limit; i++) {
      var p = list[i];
      if (!p || !p.available) continue;
      if (exclude[String(p.id)]) continue;
      var variant = null;
      var variants = p.variants || [];
      for (var v = 0; v < variants.length; v++) {
        if (variants[v].available && (!variant || variants[v].price < variant.price)) variant = variants[v];
      }
      if (!variant) continue;
      out.push({ product: p, variant: variant });
    }
    return out;
  }

  function fetchIntent(productId, intent) {
    var url =
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&limit=10&intent=' +
      encodeURIComponent(intent);
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('rec ' + r.status);
      return r.json();
    });
  }

  function recommendations(root, exclude, limit, seq) {
    var productId = root.getAttribute('data-product-id');
    if (!productId) return Promise.resolve({ items: [], heading: '' });
    return fetchIntent(productId, 'complementary').then(function (data) {
      if (pending !== seq) return null;
      var items = pickProducts(data.products || [], exclude, limit);
      if (items.length) {
        return { items: items, heading: root.getAttribute('data-heading-complementary') || '' };
      }
      return fetchIntent(productId, 'related').then(function (data2) {
        if (pending !== seq) return null;
        return {
          items: pickProducts(data2.products || [], exclude, limit),
          heading: root.getAttribute('data-heading-related') || '',
        };
      });
    });
  }

  /* ---------- render ---------- */

  function render(root, items, heading) {
    var list = root.querySelector('[data-upsell-list]');
    var headingEl = root.querySelector('[data-upsell-heading]');
    if (!list || !headingEl) return;
    headingEl.textContent = heading || '';
    list.innerHTML = '';
    if (!items.length) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    for (var i = 0; i < items.length; i++) {
      var p = items[i].product;
      var v = items[i].variant;
      var li = document.createElement('li');
      li.className = 'cart-drawer-upsell__item';
      li.setAttribute('data-product-id', String(p.id));

      var imgHtml = '';
      var src = p.featured_image;
      if (src && typeof src === 'object') src = src.url || src.src || '';
      if (typeof src === 'string' && src) {
        if (src.indexOf('//') === 0) src = 'https:' + src;
        var sep = src.indexOf('?') >= 0 ? '&' : '?';
        if (!/[?&]width=/.test(src)) src = src + sep + 'width=120';
        imgHtml = '<img class="cart-drawer-upsell__img" src="' + src + '" alt="" width="56" height="56" loading="lazy">';
      } else {
        imgHtml = '<span class="cart-drawer-upsell__img" aria-hidden="true"></span>';
      }

      var off = saleOff(p.id, v.price);
      var price = moneyFromCents(v.price);
      if (off > 0) price = '<s>' + price + '</s>' + moneyFromCents(v.price - off);

      var title = document.createElement('span');
      title.textContent = p.title || '';
      li.innerHTML =
        '<a class="cart-drawer-upsell__media" href="' + p.url + '">' + imgHtml + '</a>' +
        '<div class="cart-drawer-upsell__info">' +
        '<a class="cart-drawer-upsell__title" href="' + p.url + '">' + title.innerHTML + '</a>' +
        '<span class="cart-drawer-upsell__price">' + price + '</span>' +
        '</div>' +
        '<button type="button" class="cart-drawer-upsell__add" data-variant-id="' + v.id + '" aria-label="הוספה לסל">הוספה</button>';
      list.appendChild(li);
    }
  }

  function quickAdd(variantId, btn) {
    var cart = document.querySelector('cart-drawer');
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }
    if (btn) btn.disabled = true;
    var addUrl = (window.routes && routes.cart_add_url ? routes.cart_add_url : '/cart/add') + '.js';
    return fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw j;
          return j;
        });
      })
      .then(function (j) {
        if (cart && typeof cart.renderContents === 'function') {
          var it = (j.items && j.items[0]) || {};
          it.sections = j.sections;
          cart.classList.remove('is-empty');
          cart.renderContents(it);
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function () {
        if (btn) btn.disabled = false;
      });
  }

  function refresh() {
    var root = document.getElementById('CartDrawer-Upsell');
    if (!root) return;
    var limit = parseInt(root.getAttribute('data-limit') || '3', 10) || 3;
    if (limit < 2) limit = 2;
    if (limit > 3) limit = 3;
    var exclude = cartIdSet(root);
    var total = parseInt(root.getAttribute('data-cart-total') || '0', 10) || 0;
    var threshold = parseInt(root.getAttribute('data-threshold') || '0', 10) || 0;
    var key = [root.getAttribute('data-product-id'), root.getAttribute('data-cart-product-ids'), total, limit].join('|');
    if (root.dataset.loadedKey === key) return;

    if (threshold > 0 && total >= threshold) {
      root.hidden = true;
      root.dataset.loadedKey = key;
      return;
    }

    var seq = (pending = {});
    var gap = threshold > 0 ? threshold - total : 0;
    var source = root.getAttribute('data-gap-source');
    var cap = root.getAttribute('data-gap-capped') === 'true' ? parseInt(root.getAttribute('data-gap-max') || '0', 10) || 0 : 0;

    var gapStep =
      threshold > 0 && source
        ? loadGapData(source).then(function (products) {
            var items = pickGap(products, exclude, gap, limit, cap);
            if (!items.length) return null;
            var tpl = root.getAttribute('data-heading-gap') || 'עוד [amount] למשלוח חינם, אולי אחד מאלה?';
            return { items: items, heading: tpl.replace('[amount]', moneyFromCents(gap)) };
          })
        : Promise.resolve(null);

    gapStep
      .then(function (res) {
        if (pending !== seq) return null;
        return res || recommendations(root, exclude, limit, seq);
      })
      .then(function (result) {
        if (!result || pending !== seq) return;
        render(root, result.items, result.heading);
        root.dataset.loadedKey = key;
      })
      .catch(function () {
        if (pending === seq) root.hidden = true;
      });
  }

  function scheduleRefresh() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refresh, 120);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.cart-drawer-upsell__add');
    if (!btn) return;
    e.preventDefault();
    var vid = btn.getAttribute('data-variant-id');
    if (vid) quickAdd(vid, btn);
  });

  document.addEventListener('cart-drawer:refreshed', scheduleRefresh);
  document.addEventListener('cart-drawer:opened', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRefresh);
  } else {
    scheduleRefresh();
  }

  // Qty changes replace .drawer__inner without always firing our events
  var obsTimer = null;
  var obs = new MutationObserver(function () {
    clearTimeout(obsTimer);
    obsTimer = setTimeout(scheduleRefresh, 80);
  });
  function watch() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer) obs.observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
